/**
 * ProjectOriginSubscriber — hydrates `useProjectOriginStore` from the
 * ProjectOrigin lookup so `projectsMenu` can render its "By Origin" section
 * from live lookup data (Self / Organisation / Department / Location) instead
 * of hardcoding the four origins.
 *
 * Mirrors the sidebar badge subscribers (`<ProjectBadgeSubscriber />` et al.):
 * a role-gated, render-nothing component mounted once at the app shell. It is
 * NOT a websocket subscriber — the origin lookup is static reference data, so a
 * single fetch on mount (re-run if the role gate flips) is enough.
 */

import { useCallback, useEffect } from 'react';
import { useProjectOriginService } from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { useProjectOriginStore } from './projectOriginStore';

// Same gate the Projects domain uses elsewhere: baseline domain access OR Admin.
const PROJECT_ROLES = ['Projects Access', 'Admin'];
const DEBUG = import.meta.env.DEV;
const log = (...args: unknown[]) => {
  if (DEBUG) console.log('[ProjectOriginSubscriber]', ...args); // eslint-disable-line no-console
};

export function ProjectOriginSubscriber() {
  const { hasAnyRole } = useEnrichedAuth();
  const enabled = hasAnyRole(PROJECT_ROLES);

  const originService = useProjectOriginService();
  const setOrigins = useProjectOriginStore((s) => s.setOrigins);
  const reset = useProjectOriginStore((s) => s.reset);

  const loadOrigins = useCallback(async () => {
    try {
      const rows = await originService.getAll();
      log('loaded', rows?.length ?? 0);
      setOrigins(rows ?? []);
    } catch (err) {
      log('load failed', err);
    }
  }, [originService, setOrigins]);

  useEffect(() => {
    if (!enabled) return;
    void loadOrigins();
  }, [enabled, loadOrigins]);

  useEffect(() => () => reset(), [reset]);

  return null;
}
